import enterpriseApi from "api/enterprise";
import {GYM_COURSE_ID} from "constants/xp";
import useUser from "contexts/User/useUser";
import {useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";

const pageSize = 20;

const useEmployees = () => {
  const [page, setPage] = useState(0);
  const location = useLocation();
  const [sp] = useSearchParams();
  const scroll = useRef<number>(0);
  const user = useUser();

  // @ts-ignore
  const noAccess = !user.isEnterprise;
  const isEmployeesRoute = location.pathname.startsWith("/employees");

  const {data: _employeesUnfiltered, isLoading: loading} = useQuery({
    queryKey: ["employees"],
    queryFn: () => enterpriseApi.getEmployeees(),
    enabled: isEmployeesRoute && !noAccess,
    staleTime: 5 * 60 * 1000,
  });

  const _employees = useMemo(() => {
    const search = sp.get("search") || "";
    const ordering = sp.get("ordering") || "";
    if (!ordering) {
      return _employeesUnfiltered
        ?.sort((a, b) => {
          const aCompleted = a.milestonesCompletedList.filter((m) => m.isCompleted).length;
          const bCompleted = b.milestonesCompletedList.filter((m) => m.isCompleted).length;

          return bCompleted - aCompleted;
        })
        .filter((e) => e.name.toLowerCase().includes(search.toLowerCase()));
    }
    if (ordering === "foundations") {
      return _employeesUnfiltered
        ?.sort((a, b) => {
          const aFoundations = a.courses[GYM_COURSE_ID].completed;
          const bFoundations = b.courses[GYM_COURSE_ID].completed;

          return bFoundations - aFoundations;
        })
        .filter((e) => e.name.toLowerCase().includes(search.toLowerCase()));
    }
    if (ordering === "cleanup") {
      return _employeesUnfiltered
        ?.sort((a, b) => {
          const aCleanup = a.courses[955112].completed;
          const bCleanup = b.courses[955112].completed;

          return bCleanup - aCleanup;
        })
        .filter((e) => e.name.toLowerCase().includes(search.toLowerCase()));
    }
    if (ordering === "practice") {
      return _employeesUnfiltered
        ?.sort((a, b) => {
          const aPractice = a.courses[577780].completed;
          const bPractice = b.courses[577780].completed;

          return bPractice - aPractice;
        })
        .filter((e) => e.name.toLowerCase().includes(search.toLowerCase()));
    }
  }, [_employeesUnfiltered, sp]);

  const pages = useMemo(() => {
    if (!_employees) return 0;

    return Math.ceil(_employees.length / pageSize);
  }, [_employees]);

  const total = useMemo(() => {
    if (!_employees) return 0;

    return _employees.length;
  }, [_employees]);

  const employees = useMemo(() => {
    if (!_employees) return [];

    const start = page * pageSize;
    const end = start + pageSize;

    return _employees.slice(start, end);
  }, [_employees, page]);

  useEffect(() => {
    if (!window.location.pathname.startsWith("/employees")) return;
    scroll.current = 0;
  }, [location.search]);

  return {
    loading,
    employees,
    page,
    pages,
    setPage,
    scroll,
    total,
  };
};

export default useEmployees;
